import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import MessagingMuteToggle from '~components/Messaging/MessagingMuteToggle';
import { Skeleton } from '~components/Skeleton/Skeleton';
import useMessagingOpenConversation from '~hooks/useMessaging/useMessagingOpenConversation';
import useMessagingTyping from '~hooks/useMessaging/useMessagingTyping';
import useTranscriptReadState from '~hooks/useMessaging/useTranscriptReadState';
import {
  messagingChannelSubtitle,
  messagingChannelTitle,
} from '~pages/Messages/messagingChannelLabels';
import { useMessagingOutbox } from '~pages/Messages/MessagingOutboxContext';
import useMessagingChannelQuery from '~queries/useMessagingChannelQuery';
import useMessagingMessagesInfiniteQuery from '~queries/useMessagingMessagesInfiniteQuery';
import { ROUTES } from '~router/routePaths';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import MessageComposer from './components/MessageComposer';
import MessagingArchivedTag from './components/MessagingArchivedTag';
import MessagingAttachmentDropZone from './components/MessagingAttachmentDropZone';
import {
  MESSAGING_HEADER_AVATAR_SIZE_IN_PX,
  MessagingChannelAvatar,
} from './components/MessagingAvatar';
import MessagingChannelSubtitle from './components/MessagingChannelSubtitle';
import MessagingChannelTargetButton from './components/MessagingChannelTargetButton';
import MessagingLoadError from './components/MessagingLoadError';
import MessagingReconnectingBanner from './components/MessagingReconnectingBanner';
import MessagingTranscript from './components/MessagingTranscript';

type MessagingConversationProps = {
  channelId: string;
  onBack?: () => void;
};

/** A `reply_to_sender` send can land in another channel, so a resolved id navigates. */
const MessagingConversation = observer(
  ({ channelId, onBack }: MessagingConversationProps) => {
    const theme = useTheme();
    const { userStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);
    const navigate = useNavigate();

    const {
      channel,
      hasLoadFailed: hasChannelLoadFailed,
      isLoading: isLoadingChannel,
      isRefreshing: isRefreshingChannel,
      retryLoad: retryLoadChannel,
    } = useMessagingChannelQuery({ channelId, companyId });

    const transcript = useMessagingMessagesInfiniteQuery({ channelId, companyId });
    const {
      fetchOlder,
      hasLoadFailed,
      hasOlder,
      hasOlderLoadFailed,
      isLoading,
      isLoadingOlder,
      messages,
      retryLoad: retryLoadMessages,
    } = transcript;

    const { unreadDividerMessageId } = useTranscriptReadState({
      channel,
      channelId,
      isRefreshingChannel,
      transcript,
      viewerUserId: userStore.user?.id,
    });
    useMessagingOpenConversation(channelId);

    const { noteTyping, typers } = useMessagingTyping({
      channel,
      isLoadingMessages: isLoading,
      messages,
      viewerUserId: userStore.user?.id,
    });

    const { entriesByChannelId, retrySend } = useMessagingOutbox();
    const outboxEntries = entriesByChannelId[channelId] ?? [];

    const canModerate = Boolean(userStore.getPermissions()?.canModerateMessagingMessage);

    const onChannelResolved = useCallback(
      (resolvedChannelId: string) => {
        if (resolvedChannelId !== channelId) {
          navigate(`/${ROUTES.messages}/${resolvedChannelId}`);
        }
      },
      [channelId, navigate],
    );

    // Without the channel there is no header or composer, so a Retry that skipped it would not recover.
    const onRetryLoad = useCallback(() => {
      void retryLoadMessages();

      if (!channel) {
        void retryLoadChannel();
      }
    }, [channel, retryLoadChannel, retryLoadMessages]);

    // A failed transcript already offers one Retry that covers the conversation too.
    const showsChannelLoadError = hasChannelLoadFailed && !hasLoadFailed;

    const onRetrySend = useCallback(
      async (clientMessageId: string) => {
        if (!channel) return;

        const resolvedChannelId = await retrySend(channel, clientMessageId);
        if (resolvedChannelId) {
          onChannelResolved(resolvedChannelId);
        }
      },
      [channel, onChannelResolved, retrySend],
    );

    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: theme.semantic.surface,
            borderBottom: `1px solid ${theme.semantic.divider}`,
            display: 'flex',
            flexShrink: 0,
            gap: 1,
            minHeight: 64,
            px: 2,
            py: 1,
          }}
        >
          {onBack && (
            <IconButton aria-label={t('actions.back')} onClick={onBack} size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}

          {isLoadingChannel ? (
            <Box
              data-test-id="messaging-conversation-header-skeleton"
              sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 0.75 }}
            >
              <Skeleton height={20} variant="rectangular" width="40%" />
              <Skeleton height={12} variant="rectangular" width="25%" />
            </Box>
          ) : (
            <>
              {showsChannelLoadError && (
                <MessagingLoadError
                  message={t('messaging.load_conversation_failed')}
                  onRetry={retryLoadChannel}
                />
              )}

              {channel && (
                <>
                  <MessagingChannelAvatar
                    channel={channel}
                    size={MESSAGING_HEADER_AVATAR_SIZE_IN_PX}
                    viewerUserId={userStore.user?.id}
                  />
                  <Box
                    sx={{
                      alignItems: 'flex-start',
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 1,
                        maxWidth: '100%',
                        minWidth: 0,
                      }}
                    >
                      <Typography noWrap sx={{ minWidth: 0 }} variant="h6">
                        {messagingChannelTitle(channel)}
                      </Typography>
                      {channel.isArchived && <MessagingArchivedTag />}
                    </Box>
                    <MessagingChannelSubtitle
                      channel={channel}
                      label={messagingChannelSubtitle(channel)}
                    />
                  </Box>
                </>
              )}
            </>
          )}

          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, ml: 'auto' }}>
            {!isLoadingChannel && channel && (
              <MessagingChannelTargetButton channel={channel} />
            )}

            {channel && <MessagingMuteToggle channel={channel} key={channel.id} />}
          </Box>
        </Box>

        <MessagingReconnectingBanner />

        <MessagingAttachmentDropZone>
          <MessagingTranscript
            canModerate={canModerate}
            channel={channel}
            hasLoadFailed={hasLoadFailed}
            hasOlder={hasOlder}
            hasOlderLoadFailed={hasOlderLoadFailed}
            isLoading={isLoading}
            isLoadingOlder={isLoadingOlder}
            // Remounting on a channel switch resets the scroll anchor.
            key={channelId}
            messages={messages}
            onLoadOlder={fetchOlder}
            onRetryLoad={onRetryLoad}
            onRetrySend={onRetrySend}
            outboxEntries={outboxEntries}
            typers={typers}
            unreadDividerMessageId={unreadDividerMessageId}
            viewerUserId={userStore.user?.id}
          />

          {channel && (
            <MessageComposer
              channel={channel}
              focusOnMount
              // Remounting on a channel switch keeps a draft from leaking across conversations.
              key={`composer-${channelId}`}
              onChannelResolved={onChannelResolved}
              onTyping={noteTyping}
            />
          )}
        </MessagingAttachmentDropZone>
      </Box>
    );
  },
);

export default MessagingConversation;
